import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { ManhwaRecap } from '../types';
import { toast } from 'react-hot-toast';

const EditRecapPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recap, setRecap] = useState<ManhwaRecap | null>(null);
  const [seriesName, setSeriesName] = useState('');
  const [status, setStatus] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecap = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const fetchedRecap = await ApiService.getRecapById(id);
        setRecap(fetchedRecap);
        setSeriesName(fetchedRecap.series_name || '');
        setStatus(fetchedRecap.status || '');
        setThumbnailUrl(fetchedRecap.thumbnail_url || '');
        setError(null);
      } catch (err) {
        setError('Failed to fetch recap data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecap();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      await ApiService.updateRecap(id, {
        series_name: seriesName,
        status,
        thumbnail_url: thumbnailUrl
      });
      toast.success('Recap updated successfully!');
      navigate(`/`);
    } catch (err) {
      toast.error('Failed to update recap.');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="text-white">Loading...</div></div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen"><div className="text-red-500">{error}</div></div>;
  }

  if (!recap) {
    return <div className="flex justify-center items-center h-screen"><div className="text-white">Recap not found.</div></div>;
  }

  return (
    <div className="container mx-auto p-8 max-w-2xl bg-gray-900 text-white">
      <h1 className="text-3xl font-bold mb-6 border-b border-gray-700 pb-4">Edit Recap</h1>
      <h2 className="text-xl font-semibold text-purple-400 mb-4">{recap.title}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="seriesName" className="block text-sm font-medium text-gray-300 mb-1">
            Series Name
          </label>
          <input
            type="text"
            id="seriesName"
            value={seriesName}
            onChange={(e) => setSeriesName(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
          />
        </div>
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-1">
            Status
          </label>
          <input
            type="text"
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="e.g., Ongoing, Completed, Hiatus"
            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
          />
        </div>
        <div>
          <label htmlFor="thumbnailUrl" className="block text-sm font-medium text-gray-300 mb-1">
            Thumbnail URL
          </label>
          <input
            type="text"
            id="thumbnailUrl"
            value={thumbnailUrl}
            onChange={(e) => setThumbnailUrl(e.target.value)}
            placeholder="e.g., https://img.youtube.com/vi/VIDEO_ID/hqdefault.jpg"
            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
          />
          <p className="text-xs text-gray-500 mt-1">
            Common YouTube thumbnail qualities: maxresdefault, hqdefault, mqdefault, sddefault
          </p>
        </div>
        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)} // Go back to the previous page
            className="px-6 py-2 mr-4 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRecapPage;
